import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';
import { useCreateQuestionSet } from '../api/question-sets';

/**
 * Create a brand-new question set. Seeded with a single placeholder
 * question so the resulting draft is parseable by the orchestrator's
 * Zod schema on first save (which requires `questions.min(1)`); the
 * operator then navigates to the edit page to add real content.
 */
export function QuestionSetNewPage() {
  const navigate = useNavigate();
  const orgs = useOrgs();
  const products = useProducts();
  const create = useCreateQuestionSet();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [orgId, setOrgId] = useState('');
  const [productId, setProductId] = useState('');
  // When checked, the set is a product-wide default (orgId = null on
  // the server). Resolution at runtime prefers an org-specific set
  // over the default. Only cross-tenant admins can publish defaults
  // — the server-side check refuses tenant clients setting this to
  // true, but the UI hides the option anyway for non-cross-tenant
  // roles (operators land on the org dropdown by default).
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!isDefault && !orgId) {
      setError('Organisation is required (or check "Default for all organisations").');
      return;
    }
    if (!productId) {
      setError('Product is required.');
      return;
    }

    create.mutate(
      {
        ...(isDefault ? { is_default: true } : { org_id: orgId }),
        product_id: productId,
        name: trimmedName,
        ...(description.trim() ? { description: description.trim() } : {}),
        // Minimal valid set — a single free-text question so the
        // server's `min(1)` rule passes. Operator replaces it in the
        // edit page.
        questions: [
          {
            id: 'q1',
            prompt: 'First question — replace me in the edit page',
            required: true,
            answer: { type: 'free_text' },
          },
        ],
      },
      {
        onSuccess: (created) => {
          notifications.show({ color: 'green', message: 'Question set draft created.' });
          navigate(`/question-sets/${created.id}`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setError(parsed?.message ?? `Create failed (${err.status})`);
            return;
          }
          setError(err instanceof Error ? err.message : 'Create failed');
        },
      },
    );
  }

  return (
    <Stack gap="lg" maw={600}>
      <Anchor component={Link} to="/question-sets" size="sm">
        ← Back to question sets
      </Anchor>
      <Title order={2}>New question set</Title>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Name"
            description="A short label, e.g. screening, follow-up. Lives alongside (org, product)."
            placeholder="e.g. screening"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Textarea
            label="Description"
            placeholder="Optional. What's this set for?"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Checkbox
            label="Default for all organisations"
            description="When checked, this set applies to every organisation that uses the selected product, unless they author their own override. Requires cross-tenant admin."
            checked={isDefault}
            onChange={(e) => setIsDefault(e.currentTarget.checked)}
          />
          {!isDefault ? (
            <NativeSelect
              label="Organisation"
              description="Set owner. Cross-tenant admins must pick the target org."
              required
              value={orgId}
              onChange={(e) => setOrgId(e.currentTarget.value)}
              data={[
                { value: '', label: 'Select an organisation…' },
                ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Organisation: <em>none — applies to every org for the selected product.</em>
            </Text>
          )}
          <NativeSelect
            label="Product"
            required
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
            data={[
              { value: '', label: 'Select a product…' },
              ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
            ]}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" component={Link} to="/question-sets">
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create draft
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
}
